import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Download, Calendar, Ticket, MapPin, Clock, User, Phone, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

import { API } from "../config/api";

// Download tickets function
const downloadTickets = async (sessionId, ticketCount) => {
  try {
    toast.loading("Preparing your tickets...");
    
    const response = await fetch(`${API}/orders/by-session/${sessionId}/download-tickets`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to download tickets");
    }
    
    // Get the blob
    const blob = await response.blob();
    
    // Determine filename from response headers or default
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = ticketCount > 1 ? "tickets.zip" : "ticket.pdf";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename=([^;]+)/);
      if (match) filename = match[1].replace(/"/g, "");
    }
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.dismiss();
    toast.success(`Tickets downloaded successfully!`);
  } catch (error) {
    toast.dismiss();
    toast.error(error.message || "Failed to download tickets");
  }
};

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [orderData, setOrderData] = useState(null);
  const [polling, setPolling] = useState(true);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId && polling) {
      // First check if order already exists and is paid
      checkExistingOrder();
    }
  }, [sessionId, polling]);

  const checkExistingOrder = async () => {
    try {
      // Try to fetch order directly first (for already paid orders)
      const response = await fetch(`${API}/orders/by-session/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.order && data.order.status === "paid") {
          setOrderData(data);
          setStatus("success");
          setPolling(false);
          return;
        }
      }
      // If not found or not paid, start polling Stripe
      pollPaymentStatus();
    } catch (error) {
      // If error, fall back to polling
      pollPaymentStatus();
    }
  };

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      setPolling(false);
      return;
    }

    try {
      const response = await fetch(`${API}/checkout/status/${sessionId}`);
      if (!response.ok) throw new Error("Failed to check status");

      const data = await response.json();

      if (data.payment_status === "paid") {
        setStatus("success");
        setPolling(false);
        // Fetch full order details
        fetchOrderDetails();
      } else if (data.status === "expired") {
        setStatus("expired");
        setPolling(false);
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error polling status:", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const response = await fetch(`${API}/orders/by-session/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setOrderData(data);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    }
  };

  // Map category ID to human-readable label
  const getTicketTypeLabel = (categoryId) => {
    if (categoryId === "A") return "Adult";
    if (categoryId === "B") return "Kids";
    if (categoryId === "C") return "Infants";
    return categoryId;
  };

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-b from-kerala-cream via-white to-kerala-cream" data-testid="checkout-success-page">
      <div className="section-container py-12">
        <div className="max-w-3xl mx-auto">
          {status === "loading" && (
            <div className="text-center space-y-6 py-20">
              <div className="w-24 h-24 bg-gradient-to-br from-kerala-green/20 to-emerald-100 rounded-full flex items-center justify-center mx-auto shadow-lg">
                <div className="animate-spin w-12 h-12 border-4 border-kerala-green border-t-transparent rounded-full"></div>
              </div>
              <h1 className="font-heading text-3xl text-kerala-green">Processing Your Payment...</h1>
              <p className="text-gray-600">Please wait while we confirm your payment and generate your tickets.</p>
              <div className="flex justify-center gap-2">
                <div className="w-2 h-2 bg-kerala-green rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                <div className="w-2 h-2 bg-kerala-green rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                <div className="w-2 h-2 bg-kerala-green rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
              </div>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-8">
              {/* Success Header */}
              <div className="text-center space-y-4">
                <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-xl animate-pulse">
                  <Check className="w-12 h-12 text-white" strokeWidth={3} />
                </div>
                <h1 className="font-heading text-4xl md:text-5xl text-kerala-green">Payment Successful!</h1>
                <p className="text-gray-600 text-lg max-w-md mx-auto">
                  Thank you for your purchase! Your tickets have been generated and sent to your email.
                </p>
              </div>

              {/* Order Details Card */}
              {orderData?.order && (
                <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-kerala-gold/20 overflow-hidden">
                  {/* Order Header */}
                  <div className="bg-gradient-to-r from-kerala-green via-kerala-green to-emerald-700 p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/70 text-sm uppercase tracking-wider">Order Confirmation</p>
                        <p className="font-heading text-2xl mt-1">#{orderData.order.id?.slice(0, 8).toUpperCase()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white/70 text-sm">Total Paid</p>
                        <p className="font-heading text-3xl text-kerala-gold">
                          ${((orderData.order.amount_cents || 0) / 100).toFixed(2)} CAD
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Ticket Holder Info */}
                    <div className="bg-kerala-cream/50 rounded-xl p-5 border border-kerala-gold/20">
                      <h3 className="font-heading text-lg text-kerala-green mb-4 flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Ticket Holder Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-kerala-green/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-kerala-green" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Name</p>
                            <p className="font-semibold text-kerala-green">{orderData.order.customer_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-kerala-green/10 flex items-center justify-center">
                            <Phone className="w-5 h-5 text-kerala-green" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Phone</p>
                            <p className="font-semibold text-kerala-green">{orderData.order.customer_phone || "N/A"}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-kerala-green/10 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-kerala-green" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Email</p>
                            <p className="font-semibold text-kerala-green text-sm">{orderData.order.customer_email}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tickets List */}
                    <div>
                      <h3 className="font-heading text-lg text-kerala-green mb-4 flex items-center gap-2">
                        <Ticket className="w-5 h-5" />
                        Your Tickets ({orderData.order.tickets?.reduce((sum, t) => sum + (t.quantity || 1), 0) || 0})
                      </h3>
                      <div className="space-y-3">
                        {orderData.order.tickets?.map((ticket, index) => {
                          const ticketType = getTicketTypeLabel(ticket.category_id);
                          const priceDisplay = ticket.price_cents > 0 
                            ? `$${(ticket.price_cents / 100).toFixed(2)}` 
                            : "FREE";
                          return (
                            <div 
                              key={index}
                              className="bg-white rounded-xl p-4 border border-kerala-gold/20 flex items-center justify-between hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-kerala-gold/20 to-kerala-gold/10 rounded-lg flex items-center justify-center">
                                  <span className="text-kerala-green font-bold text-lg">{ticket.quantity || 1}</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-kerala-green text-lg">{ticketType}</p>
                                  <p className="text-sm text-gray-500">
                                    Diwali Kudumbasangamam 2026 ({ticket.slot_time})
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-kerala-green">
                                  {ticket.price_cents > 0 
                                    ? `$${((ticket.price_cents * (ticket.quantity || 1)) / 100).toFixed(2)}`
                                    : "FREE"}
                                </p>
                                <p className="text-xs text-gray-400">{ticket.quantity || 1} × {priceDisplay}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Download Tickets Section */}
                    <div className="bg-gradient-to-r from-emerald-500 to-kerala-green rounded-xl p-5 text-white shadow-lg">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                            <Download className="w-7 h-7" />
                          </div>
                          <div>
                            <h3 className="font-heading text-xl">Download Your Tickets</h3>
                            <p className="text-white/80 text-sm">Get your PDF tickets instantly - no need to wait for email</p>
                          </div>
                        </div>
                        <button
                          onClick={() => downloadTickets(sessionId, orderData.order.tickets?.reduce((sum, t) => sum + (t.quantity || 1), 0) || 0)}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-kerala-green font-semibold rounded-lg hover:bg-kerala-cream transition-colors shadow-md"
                          data-testid="download-tickets-btn"
                        >
                          <Download className="w-5 h-5" />
                          Download All Tickets (PDF)
                        </button>
                      </div>
                    </div>

                    {/* Event Details - Always show with fallback */}
                    <div className="bg-gradient-to-br from-kerala-cream/50 to-amber-50/30 rounded-xl p-5 border border-kerala-gold/20">
                      <h3 className="font-heading text-lg text-kerala-green mb-4 flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Event Details
                      </h3>
                      <div className="space-y-3">
                        <p className="font-heading text-xl text-kerala-green">Diwali Kudumbasangamam 2026</p>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4 text-kerala-gold" />
                          <span>Saturday, November 7, 2026</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4 text-kerala-gold" />
                          <span>5:00 PM - 9:00 PM</span>
                        </div>
                        <div className="flex items-start gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 text-kerala-gold mt-0.5" />
                          <div>
                            <span>Ilderton Community Centre<br />13168 Ilderton Road, Middlesex Centre, Ilderton, ON N0M 2A0</span>
                            <br />
                            <a 
                              href="https://www.google.com/maps/search/?api=1&query=Ilderton+Community+Centre+13168+Ilderton+Road"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-kerala-green hover:underline text-sm inline-flex items-center gap-1 mt-1"
                            >
                              View on Google Maps →
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Important Notes */}
                    <div className="bg-amber-50/50 rounded-xl p-5 border border-amber-200/50">
                      <h3 className="font-heading text-lg text-amber-800 mb-3">Important Information</h3>
                      <ul className="space-y-2 text-sm text-amber-700">
                        <li className="flex items-start gap-2">
                          <span className="text-amber-500 mt-0.5">•</span>
                          Your tickets have been sent to <strong>{orderData.order.customer_email}</strong>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-500 mt-0.5">•</span>
                          Please present your ticket (printed or digital) at the entrance
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-500 mt-0.5">•</span>
                          No tickets will be sold at the venue - entry for ticket holders only
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/picnic-2026">
                  <Button className="w-full sm:w-auto bg-kerala-green hover:bg-emerald-700 text-white px-8 py-6 rounded-xl font-semibold text-lg shadow-lg">
                    <Ticket className="w-5 h-5 mr-2" />
                    View Event Details
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full sm:w-auto border-2 border-kerala-green text-kerala-green hover:bg-kerala-green hover:text-white px-8 py-6 rounded-xl font-semibold text-lg">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === "timeout" && (
            <div className="text-center space-y-6 py-20">
              <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                <Ticket className="w-12 h-12 text-yellow-600" />
              </div>
              <h1 className="font-heading text-3xl text-kerala-green">Payment Processing</h1>
              <p className="text-gray-600 max-w-md mx-auto">
                Your payment is still being processed. Please check your email for confirmation or contact us if you have any questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button className="btn-primary">Contact Support</Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="border-kerala-green text-kerala-green">Back to Home</Button>
                </Link>
              </div>
            </div>
          )}

          {status === "expired" && (
            <div className="text-center space-y-6 py-20">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-red-600 text-4xl font-bold">!</span>
              </div>
              <h1 className="font-heading text-3xl text-kerala-green">Session Expired</h1>
              <p className="text-gray-600 max-w-md mx-auto">
                Your payment session has expired. Please try booking again.
              </p>
              <Link to="/picnic-2026">
                <Button className="btn-primary">Try Again</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
